import React, { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Form,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { epcService } from '@/services/epc.service';

const { Text, Paragraph } = Typography;

type Props = { projectId?: string };

export const DigitalTwinPanel: React.FC<Props> = ({ projectId }) => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [lastSimId, setLastSimId] = useState<string | null>(null);
  const [form] = Form.useForm();

  const catalogQ = useQuery({
    queryKey: ['cep-digital-twin', projectId],
    queryFn: () => epcService.getCepDigitalTwin(projectId),
    enabled: !!projectId,
    staleTime: 60_000,
  });

  const simMut = useMutation({
    mutationFn: (values: Record<string, unknown>) =>
      epcService.simulateDigitalTwin({ ...values, projectId }),
    onSuccess: (data: any) => {
      const id = data?.simulation?.id || data?.data?.simulation?.id;
      if (id) setLastSimId(String(id));
      message.success('What-if simulation completed (advisory)');
      setOpen(false);
      void qc.invalidateQueries({ queryKey: ['cep-digital-twin'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const promoteMut = useMutation({
    mutationFn: () =>
      epcService.promoteDigitalTwin({ simulationId: lastSimId, confirm: true }),
    onSuccess: () => message.success('Promoted to EOAF suggested action only'),
    onError: (e: Error) => message.error(e.message),
  });

  if (!projectId) return <Tag>Select a project for Digital Twin hooks</Tag>;

  const catalog = (catalogQ.data || {}) as any;
  const scenarios = (catalog.scenarios as any[]) || [];

  return (
    <div>
      <Space style={{ marginBottom: 12 }} wrap>
        <Button type="primary" onClick={() => setOpen(true)} disabled={catalog.disabled}>
          Run what-if
        </Button>
        <Button
          disabled={!lastSimId}
          loading={promoteMut.isPending}
          onClick={() => promoteMut.mutate()}
        >
          Promote last run (confirm)
        </Button>
        <Tag color="geekblue">CEP Wave 6</Tag>
      </Space>
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 12 }}
        message="What-if only"
        description="Simulations never mutate ERP. Promote requires explicit confirm and creates EOAF suggested actions — never Approval Runtime or finance posting."
      />
      {catalog.disabled && (
        <Alert type="error" message={catalog.message || 'Digital Twin disabled'} style={{ marginBottom: 12 }} />
      )}
      <Card size="small" title="Available scenarios" loading={catalogQ.isLoading}>
        <Table
          size="small"
          rowKey={(r) => String(r.code)}
          pagination={false}
          dataSource={scenarios}
          columns={[
            { title: 'Code', dataIndex: 'code', width: 180 },
            { title: 'Title', dataIndex: 'title' },
            { title: 'Domain', dataIndex: 'domain', width: 120 },
          ]}
        />
        <Paragraph style={{ marginTop: 8 }}>
          <Text type="secondary">{catalog.note}</Text>
        </Paragraph>
        {lastSimId && (
          <Tag color="green" style={{ marginTop: 8 }}>
            Last simulation: {lastSimId}
          </Tag>
        )}
      </Card>
      <Modal
        title="CEP what-if simulation"
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={simMut.isPending}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(v) => simMut.mutate(v)}
          initialValues={{ scenarioType: 'project_delay', delayDays: 14 }}
        >
          <Form.Item name="scenarioType" label="Scenario" rules={[{ required: true }]}>
            <Select
              options={scenarios.map((s: any) => ({
                value: s.code,
                label: `${s.code} — ${s.title}`,
              }))}
            />
          </Form.Item>
          <Form.Item name="delayDays" label="Delay days (project_delay)">
            <InputNumber min={1} max={365} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default DigitalTwinPanel;
